import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import TimerSettings from './components/TimerSettings';
import ProgressBar from './components/ProgressBar';
import WritingArea from './components/WritingArea';
import WritingHistory, { WritingSession } from './components/WritingHistory';
import { v4 as uuidv4 } from 'uuid';
import CountdownModal from './components/CountdownModal';
import ProjectSidebar from './components/ProjectSidebar';
import RulesPanel from './components/RulesPanel';

function App() {
  // Timer settings
  const [timerDuration, setTimerDuration] = useState<number>(60); // 60 seconds
  
  // Calculate pause threshold (5% of timer duration, minimum 1 second)
  const pauseThreshold = useMemo(() => {
    return Math.max(1, Math.round(timerDuration * 0.05));
  }, [timerDuration]);
  
  // Writing session state
  const [isWriting, setIsWriting] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(timerDuration);
  const [wasTextDeleted, setWasTextDeleted] = useState<boolean>(false);
  
  // Title and goal states
  const [title, setTitle] = useState<string>('');
  const [goal, setGoal] = useState<string>('');
  
  // Writing history
  const [sessions, setSessions] = useState<WritingSession[]>([]);

  // Countdown modal state
  const [showCountdown, setShowCountdown] = useState<boolean>(false);
  const [countdownValue, setCountdownValue] = useState<number>(3);
  
  // Flag to track successful completion
  const [sessionCompleted, setSessionCompleted] = useState<boolean>(false);

  // Projects and active project state
  const [projects, setProjects] = useState<{id: string, name: string, sessions: WritingSession[]}[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  
  // Writing rules state
  const [rules, setRules] = useState<{
    bannedWords: string[];
    requiredWords: string[];
    wordLengthPattern: number[] | null;
    shouldRhyme: boolean;
  }>({
    bannedWords: [],
    requiredWords: [],
    wordLengthPattern: null,
    shouldRhyme: false
  });
  
  // Load projects from localStorage when the app initializes
  useEffect(() => {
    const savedProjects = localStorage.getItem('writingFlowProjects');
    if (savedProjects) {
      // Convert string dates back to Date objects
      const parsedProjects = JSON.parse(savedProjects).map((project: any) => ({
        ...project,
        sessions: project.sessions.map((session: any) => ({
          ...session,
          timestamp: new Date(session.timestamp)
        }))
      }));
      setProjects(parsedProjects);
      
      // Set the active project to the first one if it exists
      if (parsedProjects.length > 0) {
        setActiveProjectId(parsedProjects[0].id);
        setSessions(parsedProjects[0].sessions);
      }
    } else {
      // Create a default project if none exist
      const defaultProject = {
        id: uuidv4(),
        name: 'My Writing Project',
        sessions: []
      };
      setProjects([defaultProject]);
      setActiveProjectId(defaultProject.id);
    }
  }, []);
  
  // Save projects to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('writingFlowProjects', JSON.stringify(projects));
  }, [projects]);
  
  // Reset the timer when the timer duration changes
  useEffect(() => {
    if (!isWriting) {
      setTimeRemaining(timerDuration);
    }
  }, [timerDuration, isWriting]);
  
  // Timer countdown effect
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (isWriting) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          const newTime = prev - 0.1; // Decrease by 0.1 second for smoother updates
          
          // Timer has finished
          if (newTime <= 0) {
            setIsWriting(false);
            setSessionCompleted(true);
            return 0;
          }
          
          return newTime;
        });
      }, 100); // Update every 100ms for a smoother countdown
    } else {
      // If not writing, ensure session is only marked as completed if timer reached zero
      if (timeRemaining > 0) {
        setSessionCompleted(false);
      }
    }
    
    // Cleanup interval on unmount or when writing stops
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isWriting, timeRemaining]);
  
  // Countdown effect for the 3-second countdown before starting
  useEffect(() => {
    let countdownTimer: NodeJS.Timeout | null = null;
    
    if (showCountdown && countdownValue > 0) {
      countdownTimer = setTimeout(() => {
        const newCountdownValue = countdownValue - 1;
        setCountdownValue(newCountdownValue);
        
        if (newCountdownValue === 0) {
          // Countdown finished, start the actual writing session
          setShowCountdown(false);
          setIsWriting(true);
          setWasTextDeleted(false);
          setSessionCompleted(false);
          setTimeRemaining(timerDuration);
        }
      }, 1000);
    }
    
    return () => {
      if (countdownTimer) clearTimeout(countdownTimer);
    };
  }, [showCountdown, countdownValue, timerDuration]);
  
  // Start the writing session
  const startWriting = () => {
    // Show the 3-second countdown first
    setCountdownValue(3);
    setShowCountdown(true);
  };
  
  // Stop the writing session
  const stopWriting = () => {
    setIsWriting(false);
    setShowCountdown(false);
    setSessionCompleted(false);
  };
  
  // Handle user pausing during writing
  const handlePause = useCallback(() => {
    if (isWriting) {
      // Delete text by triggering the wasTextDeleted flag
      setWasTextDeleted(true);
      // Also abort the timer
      setIsWriting(false);
      setSessionCompleted(false);
    }
  }, [isWriting]);
  
  // Handle user resuming typing
  const handleResumeTyping = useCallback(() => {
    // No action needed, just a callback for the WritingArea component
  }, []);
  
  // Handle completed writing session
  const handleCompleteWriting = useCallback((text: string) => {
    if (text.trim() !== '' && sessionCompleted) {
      const wordCount = text.trim().split(/\s+/).length;
      
      const newSession: WritingSession = {
        id: uuidv4(),
        text,
        timestamp: new Date(),
        duration: timerDuration,
        wordCount,
        title: title.trim() || 'Untitled',
        goal: goal.trim() || undefined
      };
      
      // Add the new session to the active project
      if (activeProjectId) {
        const updatedProjects = projects.map(project => {
          if (project.id === activeProjectId) {
            return {
              ...project,
              sessions: [...project.sessions, newSession]
            };
          }
          return project;
        });
        
        setProjects(updatedProjects);
        
        // Update the current sessions view
        const activeProject = updatedProjects.find(p => p.id === activeProjectId);
        if (activeProject) {
          setSessions(activeProject.sessions);
        }
      }
      
      setSessionCompleted(false);
      
      // Clear title and goal for next session
      setTitle('');
      setGoal('');
    }
  }, [timerDuration, sessionCompleted, title, goal, activeProjectId, projects]);
  
  // Handle title and goal changes
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };
  
  const handleGoalChange = (newGoal: string) => {
    setGoal(newGoal);
  };
  
  // Handle changing the active project
  const handleProjectChange = (projectId: string) => {
    setActiveProjectId(projectId);
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setSessions(project.sessions);
    }
  };
  
  // Handle creating a new project
  const handleCreateProject = (name: string) => {
    const newProject = {
      id: uuidv4(),
      name,
      sessions: []
    };
    setProjects([...projects, newProject]);
    setActiveProjectId(newProject.id);
    setSessions([]);
  };
  
  // Handle updating writing rules
  const handleRuleChange = (updatedRules: typeof rules) => {
    setRules(updatedRules);
  };
  
  // Get the active project name
  const activeProjectName = useMemo(() => {
    if (!activeProjectId) return 'Writing Flow';
    const activeProject = projects.find(p => p.id === activeProjectId);
    return activeProject ? activeProject.name : 'Writing Flow';
  }, [activeProjectId, projects]);
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Left Sidebar - Projects */}
      <ProjectSidebar 
        projects={projects}
        activeProjectId={activeProjectId}
        onProjectChange={handleProjectChange}
        onCreateProject={handleCreateProject}
      />
      
      {/* Main Content */}
      <div className="flex-1 py-8 px-4 overflow-auto">
        <div className="container mx-auto max-w-3xl">
          <header className="mb-6 text-center">
            <h1 className="text-3xl font-bold text-indigo-700">{activeProjectName}</h1>
            <p className="text-gray-600">Keep writing or lose everything</p>
          </header>
          
          <main className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Title Input Only */}
            <div className="p-4 title-input-area">
              <div className="mb-0">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  disabled={isWriting}
                  placeholder="Give your writing a title"
                  className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                />
              </div>
            </div>
            
            {/* Writing History */}
            <div className="max-h-[50vh] overflow-y-auto border-b border-gray-200">
              {sessions.length > 0 ? (
                <WritingHistory sessions={sessions} />
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p className="mb-2 text-lg">No writing sessions yet</p>
                  <p className="text-sm">Complete your first writing session to see it here</p>
                </div>
              )}
            </div>
            
            <div className="p-6">
              {/* Timer Settings */}
              <TimerSettings 
                timerDuration={timerDuration}
                onTimerDurationChange={setTimerDuration}
                isWriting={isWriting}
              />
              
              {/* Goal Input Only */}
              <div className="goal-input">
                <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
                  Writing Goal
                </label>
                <input
                  type="text"
                  id="goal"
                  value={goal}
                  onChange={(e) => handleGoalChange(e.target.value)}
                  disabled={isWriting}
                  placeholder="What do you want to achieve with this writing? (optional)"
                  className="block w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                />
              </div>
              
              {/* Rules Panel - Inline instead of sidebar */}
              <RulesPanel 
                rules={rules}
                onRuleChange={handleRuleChange}
                isWriting={isWriting}
              />
              
              {/* Progress Bar */}
              <ProgressBar 
                currentTime={timeRemaining}
                totalTime={timerDuration}
                isActive={isWriting}
              />
              
              {/* Writing Area */}
              <WritingArea 
                isActive={isWriting}
                onPause={handlePause}
                onResumeTyping={handleResumeTyping}
                pauseThreshold={pauseThreshold}
                onComplete={handleCompleteWriting}
                wasTextDeleted={wasTextDeleted}
                setWasTextDeleted={setWasTextDeleted}
                title={title}
                goal={goal}
                rules={rules}
              />
              
              {/* Controls */}
              <div className="flex justify-center mt-4">
                {!isWriting && !showCountdown ? (
                  <button
                    onClick={startWriting}
                    className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                  >
                    Start Writing
                  </button>
                ) : (
                  <button
                    onClick={stopWriting}
                    className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>
          </main>
          
          <footer className="mt-8 text-center text-gray-500 text-sm">
            <p>Writing Flow &copy; {new Date().getFullYear()}</p>
          </footer>
        </div>
      </div>

      {/* Countdown Modal */}
      <CountdownModal show={showCountdown} countdownValue={countdownValue} />
    </div>
  );
}

export default App;
